import React, { useMemo } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { AGE_GROUP_FIELDS } from '../../../lib/queries/age-group.queries';
import { PLAYER_FIELDS } from '../../../lib/queries/player.queries';
import { POSITION_FIELDS } from '../../../lib/queries/position.queries';
import { GET_SESSION_ATTENDANCE_RESPONSES } from '../../../lib/queries/attendance-response.queries';
import { useSnackbar } from 'notistack';

// Material UI
import CheckInContainer from './check-in.container';
import { getAuthUser } from '../../../lib/services/auth.service';
import {
  buildAssignJersey, buildRemoveCheckedInPlayer, buildUnassignJersey,
} from '../../../lib/services/assessment-session.service';
import { getCurrMember } from '../../../lib/services/member.service';
import _ from 'lodash';
import { buildUpdatePlayerAssessment } from '../../../lib/services/player-assessment.service';
import Typography from '@material-ui/core/Typography';
import { useCompanySport, useCompanySportTerminology } from '../../../lib/stores/company.store';
import { pluralTerm } from '../../../lib/helpers/term.helper';
import { SportTerminology } from '../../../lib/types';
import { hasAnyRoles } from '../../../lib/services/user.service';
import { HOME_ROUTE, ROLES } from '../../../../_configuration';
import { Redirect } from 'react-router';
import { SEND_COMMUNICATION } from '../../../lib/queries/email-log.queries';
import { useOperationErrorHandler } from '../../../lib/hooks/use-operation-error-handler';
import { withFailureReport } from '../../../lib/helpers/operation-failure.helpers';
import { getMessages } from './check-in.messages';

export const GET_CHECK_IN = gql`
  query GetCheckIn($assessmentSessionId: ID!) {
    assessmentSession(_id: $assessmentSessionId) {
      _id
      location
      area
      address
      date
      start
      assessmentId
      ageGroupId
      ageGroup {
        ...AgeGroupFields
      }
      playerAssessments {
        _id
        jerseyColour
        jerseyNumber
        player {
          ...PlayerFields
        }
        position {
          ...PositionFields
        }
        ageGroup {
          _id
        }
        adjustedScore
        deletedAt
      }
      jerseys
      teams
      checkedInPlayerIds
      isAttendanceRequested
      retainJerseyAssignments
    }
  }

  ${AGE_GROUP_FIELDS}
  ${PLAYER_FIELDS}
  ${POSITION_FIELDS}
`;

const GET_CHECK_IN_SELECTS = gql`
  query GetCheckInSelects($companyId: ID!, $assessmentId: ID!, $ageGroupId: ID!, $includeGlobalForSport: SPORT) {
    playerAssessments(assessmentId: $assessmentId, ageGroupIds: [$ageGroupId]) {
      _id
      jerseyColour
      jerseyNumber
      player {
        ...PlayerFields
      }
      position {
        ...PositionFields
      }
    }
    positions(companyId: $companyId, includeGlobalForSport: $includeGlobalForSport) {
      _id
      name
    }
    company(_id: $companyId) {
      _id
      useJerseyNotifications
    }
  }
  
  ${PLAYER_FIELDS}
  ${POSITION_FIELDS}
`;

export const UPDATE_ASSESSMENT_SESSION_PLAYER_ASSESSMENTS = gql`
  mutation UpdateAssessmentSessionPlayerAssessments($_id: ID!, $data: UpdateAssessmentSessionInput!, $notify: Boolean) {
    updateAssessmentSession(_id: $_id, data: $data, notify: $notify) {
      _id
      playerAssessments {
        _id
        player {
          ...PlayerFields
        }
      }
      checkedInPlayerIds
    }
  }
  
  ${PLAYER_FIELDS}
`;

interface ICheckInGraphQL {
  assessmentSessionId: string;
}

export const CheckInGraphQL: React.FC<ICheckInGraphQL> = ({ assessmentSessionId }) => {
  const sport = useCompanySport()
  const termLookup = useCompanySportTerminology()
  const { enqueueSnackbar } = useSnackbar();
  const messages = useMemo(() => getMessages(termLookup), [termLookup]);
  // These writes are tracked, so a failure the user never hears about is lost silently.
  const reportFailure = useOperationErrorHandler(messages.errChangeNotSaved);

  const user = getAuthUser();

  const { member } = getCurrMember();
  const { assignJersey } = buildAssignJersey();
  const { removeCheckedInPlayer } = buildRemoveCheckedInPlayer();
  const { unassignJersey } = buildUnassignJersey();
  const { updatePlayerAssessment } = buildUpdatePlayerAssessment();

  const { data, loading, error } = useQuery(GET_CHECK_IN, {
    variables: { assessmentSessionId },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-and-network'
  });

  const baseSession = data ? data.assessmentSession as IAssessmentSession : null;
  const retainJerseyAssignments = !!baseSession?.retainJerseyAssignments;

  const { data: rsvpData } = useQuery(GET_SESSION_ATTENDANCE_RESPONSES, {
    variables: { _id: assessmentSessionId },
    skip: !baseSession?.isAttendanceRequested,
    fetchPolicy: 'cache-and-network',
  });

  // Merge RSVP responses into the session so children read `attendanceResponses`
  // uniformly; memoized for a stable identity (descendants compare `session` by ref).
  const session = useMemo(() => (
    baseSession ? {
      ...baseSession,
      attendanceResponses: _.get(rsvpData, 'assessmentSession.attendanceResponses', []),
    } : null
  ), [baseSession, rsvpData]);

  const { data: selectData, loading: selectLoading, error: selectError } = useQuery(GET_CHECK_IN_SELECTS, {
    skip: !session || !user?.settings.activeCompanyId,
    variables: {
      companyId: user?.settings.activeCompanyId,
      assessmentId: session?.assessmentId,
      ageGroupId: session?.ageGroupId,
      includeGlobalForSport: sport
    },
    fetchPolicy: 'cache-and-network'
  });

  const positions = (selectData && selectData.positions) || [];
  const playerAssessments = (selectData && selectData.playerAssessments) || [];
  const company = selectData && selectData.company;

  const [updateAssessmentSession] = useMutation(UPDATE_ASSESSMENT_SESSION_PLAYER_ASSESSMENTS, {
    context: {
      serializationKey: 'MUTATION',
      tracked: true,
    },
  });

  const [sendCommunication] = useMutation(SEND_COMMUNICATION);
  const handleAddPlayerAssessment = (playerAssessment: IPlayerAssessment) => {
    const nextPlayerAssessments = [...session?.playerAssessments || []];
    const nextPlayerAssessmentIds = [..._.map(nextPlayerAssessments, '_id'), playerAssessment._id];

    return updateAssessmentSession({
      variables: { _id: assessmentSessionId, data: { playerAssessmentIds: nextPlayerAssessmentIds }, notify: false },
      update: (cache, { data }) => {
        cache.modify({
          id: `AssessmentSession:${assessmentSessionId}`,
          fields: {
            playerAssessments: () => [...nextPlayerAssessments, playerAssessment]
          }
        });
      }
    });
  };

  const handleSaveTeams = (config: string[][]) => {
    return updateAssessmentSession({
      variables: { _id: assessmentSessionId, data: { teams: { config } } }
    });
  };

  const handleRemovePlayerAssessment = (playerAssessment: IPlayerAssessment) => {
    const nextPlayerAssessments = [...session?.playerAssessments || []];
    _.remove(nextPlayerAssessments, currPlayerAssessment => currPlayerAssessment._id === playerAssessment._id);
    const nextPlayerAssessmentIds = _.map(nextPlayerAssessments, '_id');

    // The server drops the athlete from check-in in the same write; the response carries checkedInPlayerIds.
    return updateAssessmentSession({
      variables: {_id: assessmentSessionId, data: {playerAssessmentIds: nextPlayerAssessmentIds}, notify: false},
      update: (cache) => {
        cache.modify({
          id: `AssessmentSession:${assessmentSessionId}`,
          fields: {
            playerAssessments: () => nextPlayerAssessments
          }
        });
      }
    });
  };

  const handleUpdatePosition = (playerAssessment: IPlayerAssessment, positionId: string) =>
    updatePlayerAssessment(playerAssessment._id, { positionId });

  const handleSendJerseyNotification = async (playerAssessmentId: string, sendNotification: boolean) => {
    if (sendNotification && session) {
      const playerAssessment = session.playerAssessments?.find(pa => pa._id === playerAssessmentId);
      if (playerAssessment) {
        return sendCommunication({
          variables: {
            assessmentId: session.assessmentId,
            ageGroupIds: [session.ageGroupId],
            type: 'JERSEY_NUMBER',
            resend: false,
            playerAssessmentIds: [playerAssessmentId],
            // The screen knows which ice time it is running; without it the
            // server guesses, and the confirmation can name the wrong one.
            sessionId: session._id
          }
        });
      }
    }
  };

  const handleResetJerseys = async () => {
    if (session) {
      const checkedInPlayerIds = session.checkedInPlayerIds || [];
      const uncheckedInPlayerIds = (session.playerAssessments || [])
        .filter(pa => !checkedInPlayerIds.includes(pa._id))
        .map(pa => pa._id);
      
      if (uncheckedInPlayerIds.length === 0) {
        enqueueSnackbar('No jersey assignments to reset', { variant: 'info' });
        return;
      }

      try {
        // Clear jersey assignments only for unchecked-in players
        const promises = uncheckedInPlayerIds.map(playerAssessmentId => 
          unassignJersey(session._id, playerAssessmentId)
        );
        
        await Promise.all(promises);
        
        // Show success toast like rebalance teams
        enqueueSnackbar('All jerseys reset successfully!', { variant: 'success' });
      } catch (error) {
        console.error('Error resetting jerseys:', error);
        enqueueSnackbar('Error resetting jerseys', { variant: 'error' });
      }
    }
  };

  // Assigning rethrows so the jersey dialog stops before sending a notification for a jersey that didn't save.
  const reportedAssignJersey = withFailureReport(assignJersey, reportFailure, 'assignJersey', { message: messages.errJerseyNotSaved, rethrow: true });
  const reportedRemoveCheckedInPlayer = withFailureReport(removeCheckedInPlayer, reportFailure, 'removeCheckedInPlayer', { message: messages.errCheckOutNotSaved });
  const reportedAddPlayer = withFailureReport(handleAddPlayerAssessment, reportFailure, 'addPlayerAssessment', { message: messages.errRosterNotSaved });
  const reportedRemovePlayer = withFailureReport(handleRemovePlayerAssessment, reportFailure, 'removePlayerAssessment', { message: messages.errRosterNotSaved });
  const reportedSaveTeams = withFailureReport(handleSaveTeams, reportFailure, 'saveTeams', { message: messages.errTeamsNotSaved });
  const reportedUpdatePosition = withFailureReport(handleUpdatePosition, reportFailure, 'updatePosition', { message: messages.errPositionNotSaved });
  const reportedSendJerseyNotification = withFailureReport(handleSendJerseyNotification, reportFailure, 'sendJerseyNotification', { message: messages.errJerseyNotificationNotSent });

  const hasAssessmentAccess = hasAnyRoles({roles: [ROLES.CHECK_IN, ROLES.SCORING], assessmentId: session?.ageGroupId})

  if (loading && !session) return <Typography>Loading...</Typography>;
  if (!session) return <Typography>Unable to check in {pluralTerm(termLookup[SportTerminology.ATHLETE])} at this time. Please try again later</Typography>;

  if (session && !hasAssessmentAccess) return <Redirect to={HOME_ROUTE} />

  return <CheckInContainer
    loading={loading}
    selectLoading={selectLoading}
    retainJerseyAssignments={retainJerseyAssignments}
    fetchError={Boolean(error) || Boolean(selectError)}
    session={session}
    positions={positions}
    playerAssessments={playerAssessments}
    assignJersey={reportedAssignJersey}
    removeCheckedInPlayer={reportedRemoveCheckedInPlayer}
    member={member}
    removePlayer={reportedRemovePlayer}
    addPlayer={reportedAddPlayer}
    saveTeams={reportedSaveTeams}
    updatePosition={reportedUpdatePosition}
    companyUseJerseyNotifications={company?.useJerseyNotifications}
    onSendJerseyNotification={reportedSendJerseyNotification}
    resetJerseys={handleResetJerseys}
  />;
};

